import type {FC} from 'react';

import {useI18n} from '@yourcoach/i18n';

import type {GetComponentProps} from '../../../../utils/utility-types';
import type {Props} from './types';

import {AvatarPlaceholder} from '../../../../uikit/AvatarPlaceholder';
import {Clickable} from '../../../../uikit/Clickable';
import {Text} from '../../../../uikit/Text';
import * as S from './styles';

export const CourseListItemJSX: FC<Props> = ({
  buttons,
  client,
  clientsCount = 0,
  groupSize = 0,
  isArchived = false,
  isCoachLayout = false,
  onClick,
  onDeleteClick,
  onUpdateClick,
  status,
  subtitle,
  title,
}) => {
  const {t} = useI18n();
  const withSpaceBetweenStatusAndGroupSize = isCoachLayout && groupSize > 1;

  const withStopPropagationClick: (
    handler: GetComponentProps<typeof S.ActionButton>['onClick'],
  ) => GetComponentProps<typeof S.ActionButton>['onClick'] =
    handler => action => {
      action.event.DOMEvent?.stopPropagation();
      handler(action);
    };

  const isIndividual = groupSize === 1;

  return (
    <S.ClickableWrapper disabled={!onClick} onClick={onClick}>
      <S.Container>
        <S.Row>
          <S.CalendarIcon />
          <S.MainInfo>
            <S.Title isArchived={isArchived}>{title}</S.Title>
            {subtitle ? <S.Subtitle>{subtitle}</S.Subtitle> : null}
          </S.MainInfo>
          <S.Status>{status ?? ''}</S.Status>
          {withSpaceBetweenStatusAndGroupSize ? <S.Space /> : null}
          {groupSize > 1 ? (
            <S.GroupMembersInfo>
              {isArchived ? (
                <S.ArchiveGroupIcon isCoachLayout={isCoachLayout} />
              ) : (
                <S.GroupIcon isCoachLayout={isCoachLayout} />
              )}
              <S.GroupCapacity
                isCoachLayout={isCoachLayout}
                isGray={isArchived || clientsCount === groupSize}
              >
                {`${clientsCount ?? 0}/${groupSize}`}
              </S.GroupCapacity>
            </S.GroupMembersInfo>
          ) : null}
          {client ? (
            <S.ClientInfo>
              <S.ClientAvatar
                placeholder={<AvatarPlaceholder text={client.name} />}
                src={client.avatarUrl ?? ''}
              />
              <Text weight="semibold">{client.name}</Text>
            </S.ClientInfo>
          ) : null}
          <S.ButtonsContainer>
            {buttons?.map(({onClick: onButtonClick, text, ...rest}) => (
              <S.ActionButton
                isCoachLayout={isCoachLayout}
                key={text}
                onClick={
                  onButtonClick
                    ? withStopPropagationClick(onButtonClick)
                    : undefined
                }
                {...rest}
              >
                {text}
              </S.ActionButton>
            ))}
            {onDeleteClick ? (
              <Clickable onClick={withStopPropagationClick(onDeleteClick)}>
                <S.DeleteIcon />
              </Clickable>
            ) : null}
            {onClick && !onDeleteClick ? <S.NextArrowIcon /> : null}
          </S.ButtonsContainer>
        </S.Row>
        {onUpdateClick ? (
          <S.UpdateContainer>
            <S.UpdateDescription>
              {
                /**
                 * @i18n_parser
                 *
                 * t('shared:update_program_details_clients_reinvited.individual')
                 * t('shared:update_program_details_clients_reinvited.group')
                 */
                t(
                  `shared:update_program_details_clients_reinvited.${isIndividual ? 'individual' : 'group'}`,
                )
              }
            </S.UpdateDescription>
            <S.ActionButton
              isCoachLayout
              onClick={withStopPropagationClick(onUpdateClick)}
            >
              {t('shared:update')}
            </S.ActionButton>
          </S.UpdateContainer>
        ) : null}
      </S.Container>
    </S.ClickableWrapper>
  );
};
